// Camera QR scan for equipment identification — the "stand in front of the machine" flow.
// Uses the native BarcodeDetector (Chrome/Edge/Android — the shop-floor devices); browsers
// without it (Safari desktop) simply don't get the button, the text search stays.
// Accepts kiosk-label URLs (?equnr=...) as well as plain equipment numbers in the QR.
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ScanLine, X } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';

export function parseEquipmentFromQr(raw) {
  if (!raw) return null;
  try {
    const url = new URL(raw);
    const p = url.searchParams.get('equnr');
    if (p) return p;
  } catch { /* not a URL */ }
  // SAP EQUNR is CHAR18, not numeric. Customers on an EXTERNAL equipment number range routinely
  // use alphanumeric asset tags (PUMP-042, A-1140-07), and functional-location labels are
  // alphanumeric by definition — a digits-only pattern rejected perfectly valid labels, silently
  // (PM code audit finding 32). The character set is what SAP itself accepts in an object number.
  const m = String(raw).trim().match(/^[A-Za-z0-9._\-/]{1,18}$/);
  return m ? m[0] : null;
}

export default function ScanButton({ onScan }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const stopRef = useRef(false);

  const supported = typeof window !== 'undefined' && 'BarcodeDetector' in window;

  const stop = useCallback(() => {
    stopRef.current = true;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
    }
    setOpen(false);
  }, []);

  useEffect(() => () => stop(), [stop]);

  const start = async () => {
    setError(null);
    setOpen(true);
    stopRef.current = false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      const video = videoRef.current;
      video.srcObject = stream;
      await video.play();
      const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
      const tick = async () => {
        if (stopRef.current) return;
        try {
          const codes = await detector.detect(video);
          const hit = codes.map((c) => parseEquipmentFromQr(c.rawValue)).find(Boolean);
          if (hit) {
            stop();
            onScan(hit);
            return;
          }
        } catch { /* keep polling */ }
        setTimeout(tick, 250);
      };
      tick();
    } catch (e) {
      setError(e.message);
    }
  };

  if (!supported) return null;

  return (
    <>
      <button type="button" onClick={start} aria-label={t('pm.scan.open')}
        className={`p-2 rounded-xl ${isDarkMode ? 'bg-white/10 text-white/70 hover:text-white' : 'bg-slate-100 text-slate-500 hover:text-slate-800'}`}>
        <ScanLine size={20} />
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center p-4">
          <video ref={videoRef} playsInline muted className="w-full max-w-md rounded-2xl" />
          <p className="text-white/70 text-sm mt-3">{t('pm.scan.hint')}</p>
          {error && <p className="text-rose-300 text-sm mt-2">{error}</p>}
          <button type="button" onClick={stop}
            className="mt-4 flex items-center gap-2 text-white bg-white/10 rounded-full px-5 py-2">
            <X size={18} /> {t('pm.common.back')}
          </button>
        </div>
      )}
    </>
  );
}
